import axios from 'axios';
import { contractorAuth, portalContractor } from './contractorAuth';

/**
 * The service catalogue - "Request something" (migration 124) - from the
 * browser: the calls, and the words the catalogue screens share.
 *
 * WHO IS ASKING. A member of staff is signed by the axios interceptor; a
 * contractor on a portal token sends it (contractorAuth); an administrator
 * looking through View as at a contractor names them (as_contractor), as the
 * portal's other pages do, and is answered as that contractor would be.
 * The server decides what anybody may see and ask for; this only asks.
 */
const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/** The axios config for the person asking: their token, or the contractor viewed. */
export function askerConfig(extra = {}) {
  const who = portalContractor();
  const auth = contractorAuth();
  if (who?.id && who.viewing) return { ...extra, params: { ...(extra.params || {}), as_contractor: who.id } };
  return auth ? { ...extra, ...auth } : extra;
}
const asker = () => portalContractor();
const withAs = (body) => {
  const who = asker();
  return who?.id && who.viewing ? { ...body, as_contractor: who.id } : body;
};

export const catalogueApi = {
  list: () => axios.get(`${API}/catalogue`, askerConfig()).then((r) => r.data),
  item: (id) => axios.get(`${API}/catalogue/items/${id}`, askerConfig()).then((r) => r.data),
  quote: (id, answers, approverId) => axios.post(`${API}/catalogue/items/${id}/quote`,
    withAs({ answers, approver_id: approverId || null }), askerConfig()).then((r) => r.data),
  request: (id, answers, approverId) => axios.post(`${API}/catalogue/items/${id}/request`,
    withAs({ answers, approver_id: approverId || null }), askerConfig()).then((r) => r.data),
  mine: () => axios.get(`${API}/catalogue/mine`, askerConfig()).then((r) => r.data.requests || []),
  ticket: (ticketId, cfg) => axios.get(`${API}/catalogue/tickets/${ticketId}`, cfg).then((r) => r.data.request),
  step: (ticketId, stepId, body) => axios.patch(`${API}/catalogue/tickets/${ticketId}/steps/${stepId}`, body).then((r) => r.data.request),
  run: (ticketId, stepId, body) => axios.post(`${API}/catalogue/tickets/${ticketId}/steps/${stepId}/run`, body || {}).then((r) => r.data),

  // Keeping it (administrators).
  adminList: () => axios.get(`${API}/catalogue/admin/items`).then((r) => r.data.items || []),
  adminItem: (id) => axios.get(`${API}/catalogue/admin/items/${id}`).then((r) => r.data),
  options: () => axios.get(`${API}/catalogue/admin/options`).then((r) => r.data),
  record: () => axios.get(`${API}/catalogue/admin/record`).then((r) => r.data.history || []),
  create: (body) => axios.post(`${API}/catalogue/admin/items`, body).then((r) => r.data),
  update: (id, body) => axios.patch(`${API}/catalogue/admin/items/${id}`, body).then((r) => r.data),
  remove: (id) => axios.delete(`${API}/catalogue/admin/items/${id}`).then((r) => r.data),

  // Line managers (Settings -> Users).
  managers: () => axios.get(`${API}/line-managers`).then((r) => r.data),
  setManager: (userId, managerId) => axios.put(`${API}/line-managers/${userId}`, { manager_id: managerId || null }).then((r) => r.data)
};

/** The sentence a refusal carries, or `fallback`. */
export const errorOf = (e, fallback = 'That did not work.') => e?.response?.data?.error || fallback;
/** The migration this server still needs, when it says so. */
export const needsMigration = (e) => e?.response?.status === 503 && e?.response?.data?.missing === 'catalogue';

/** Where a request is, in the requester's words. */
export const STAGE = {
  waiting: { label: 'Waiting for approval', tone: 'pending' },
  sent_back: { label: 'Sent back to you', tone: 'pending' },
  refused: { label: 'Refused', tone: 'bad' },
  in_progress: { label: 'Being done', tone: 'open' },
  done: { label: 'Done', tone: 'good' }
};

export const FIELD_TYPE_LABEL = {
  text: 'Short text',
  textarea: 'Long text',
  number: 'Number',
  date: 'Date',
  select: 'Choice',
  checkbox: 'Tick box',
  person: 'A member of staff',
  licence: 'A licence from the register'
};

export const ROUTE_LABEL = {
  none: 'No approval',
  named: 'A named approver',
  requester: 'The requester picks',
  manager: 'The requester’s line manager'
};

/**
 * A small set of line icons, drawn in currentColor so they follow the theme.
 * The name is what an item stores (catalogue_items.icon); anything unknown
 * is the parcel.
 */
export const ICONS = {
  laptop: 'M4 6h16v9H4zM2 18h20',
  monitor: 'M3 4h18v12H3zM9 20h6M12 16v4',
  software: 'M4 4h16v16H4zM8 9l-2 3 2 3M16 9l2 3-2 3',
  key: 'M8 16a4 4 0 1 1 3.5-6H21v3h-2v2h-2v-2h-5.5A4 4 0 0 1 8 16z',
  'person-plus': 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21c0-4 3-6 7-6s7 2 7 6M19 8v6M16 11h6',
  phone: 'M7 2h10v20H7zM11 18h2',
  card: 'M3 6h18v12H3zM3 10h18',
  parcel: 'M3 7l9-4 9 4v10l-9 4-9-4zM3 7l9 4 9-4M12 11v10',
  printer: 'M6 9V3h12v6M6 18H4v-7h16v7h-2M8 14h8v7H8z',
  headset: 'M4 14v-2a8 8 0 0 1 16 0v2M4 14h3v6H4zM17 14h3v6h-3z'
};
export const ICON_NAMES = Object.keys(ICONS);
